import { ReelContainer } from '@/containers/reels/ReelContainer'
import { ReelsContainerHelper } from '@/containers/reels/ReelsContainerHelper'
import { ReelsStripConfig } from '@/containers/reels/ReelsStripConfig'
import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import { SymbolContainer } from '@/containers/symbols/SymbolContainer'
import { SymbolLayerType } from '@/containers/symbols/SymbolLayerType'
import { SymbolType } from '@/containers/symbols/SymbolType'
import Facade from '@/Facade'
import {
    CascadeSpinData,
    FreeSpinsActivateData,
    IInitReelsData,
    IReelsTimingsConfig,
    ISpinResponse,
    IStoppingReelsData,
    ShowRegularWinningsData,
    SymbolData,
} from '@/modules'
import { MobileSwipeSpinController, UIAPI, UIEvent } from '@dragonframework/modules'
import GameSignals from '@/signals/GameSignals'
import * as PIXI from 'pixi.js'
import gsap from 'gsap'
import { ContainersNames } from '@/containers/ContainersNames'

export class ReelsContainer extends PIXI.Container {
    private static readonly BASE_GAME_MASK_Y = 46
    private static readonly FREE_SPINS_MASK_Y = 32
    private static readonly REELS_MASK_X = -2
    private static readonly REELS_MASK_DRAW_Y = -26
    private static readonly REELS_MASK_WIDTH = 1128
    private static readonly REELS_MASK_HEIGHT = 777.5

    private _pendingRegularOutAnimations: number = 0
    protected _symbolLayers: PIXI.Container[]
    protected _reels: ReelContainer[]
    protected _reelsContainerTopUnmasked: PIXI.Container
    protected _reelsContainer: PIXI.Container
    protected _reelsContainerBotUnmasked: PIXI.Container
    protected _anticipationIndices: number[]
    protected _activeReelsStrip: number[][]
    protected _scattersCount: number
    protected _isSpinning: boolean
    protected _isQuickStop: boolean
    protected _immediatelyStopped: boolean
    protected _speedSettings: IReelsTimingsConfig
    protected _cropMask: PIXI.Graphics
    protected _cascadeIndex: number
    protected _cascadeSpinsCount: number
    protected _isFirstWildAnimation: boolean
    private _mobileSwipeSpinController: MobileSwipeSpinController | null = null
    private _isMobileSwipeSpinFeatureEnabled: boolean = false
    private _hasShownMobileSwipeSpinPopup: boolean = false

    public constructor() {
        super()
        GameSignals.createReels.connect(this.onCreateReels)
        GameSignals.startSpin.connect(this.onStartSpin)
        GameSignals.reelJustTouched.connect(this.onReelJustTouched)
        GameSignals.stoppingReels.connect(this.onSpinStoping)
        GameSignals.immediatelyStop.connect(this.onImmediateStop)
        GameSignals.errorStop.connect(this.onErrorStop)
        GameSignals.showFreeSpinsIntro.connect(this.onFreeSpinsIntro)
        GameSignals.showFreeSpinsSummaryComplete.connect(this.onFreeSpinsComplete)
        GameSignals.activateScatters.connect(this.onActivateScatters)
        GameSignals.showRegularWin.connect(this.onShowRegularWin)
        GameSignals.cascadeSpin.connect(this.onCascadeSpin)
        GameSignals.multiplierState.connect(this.onMultiplierState)
        GameSignals.finalState.connect(this.onFinalState)
        GameSignals.enableSwipeFeature.connect(this.onEnableSwipeFeature)
        GameSignals.swipeFeaturePopupShown.connect(this.onSwipeFeaturePopupShown)
        // Test splash HP overlay animation disabled
        // GameSignals.completeSplashScreenState.connect(this.onCompleteSplashScreenState)
        GameSignals.spinResponse.connect((data: ISpinResponse) => {
            this._cascadeSpinsCount = data.winning_combinations.length - 1
        })

        this.changeSpinSettings(Facade.isTurboSpin)
        this._isFirstWildAnimation = true
    }

    protected onFinalState = () => {
        this._reels.forEach((reel: ReelContainer) => {
            gsap.killTweensOf(reel)
            reel.stopScattersAnimation()
        })
    }

    protected onCascadeSpin = (data: CascadeSpinData) => {
        this._cascadeIndex++
        let suspenseReelIndex = 0
        this.symbolsShadowOut()
        let delay: number = 0
        const reelsWinPositions: number[] = []
        let cascadeSpinIndex: number = 0
        let suspenseDelay: number = 0
        const suspenseReelsIndexes: number[] = []

        if (!data.cascadeReels.length) {
            GameSignals.completeCascadeSpin.emit()
            return
        }

        data.cascadeReels.forEach((reelIndex: number, index: number) => {
            const reel: ReelContainer = this._reels[reelIndex]
            const cascadeReelsCount: number = data.cascadeReels.length

            delay += this._speedSettings.DELAY_BETWEEN_CASCADE_REELS

            const reelWinPositions: number[][] = data.winPositions.filter((position: number[]) => {
                return position[0] === reelIndex
            })

            reelsWinPositions.push(reelWinPositions.length)
            reel.updateHiddenSymbols(data.slotGrid[reelIndex], reelWinPositions)

            if (data.suspenseSpin) {
                // suspenseDelay += Facade.anticipationConfig.ANTICIPATION_REEL_TIME
                // gsap.to(reel, {
                //     duration: suspenseDelay,
                //     onComplete: () => {
                //         const duration: number = reel.suspenseSpinStage1() + Facade.anticipationConfig.DELAY_BETWEEN_STAGE1_AND_STAGE2 // this parameter can be like positive as negative
                //
                //         gsap.to(this, {
                //             duration,
                //             onComplete: () => {
                //                 suspenseReelIndex++
                //                 reel.suspenseSpinStage2(suspenseReelIndex === cascadeReelsCount)
                //             }
                //         })
                //     }
                // })
                // return
                suspenseReelsIndexes.push(reelIndex)
                gsap.to(reel, {
                    duration: delay,
                    onComplete: () => {
                        const delayBeforeSuspense: number =
                            reel.suspenseSpinStage1() + Facade.anticipationConfig.DELAY_BETWEEN_STAGE1_AND_STAGE2

                        if (index === data.cascadeReels.length - 1) {
                            suspenseReelsIndexes.forEach((reelIndex: number) => {
                                const reel: ReelContainer = this._reels[reelIndex]

                                gsap.to(reel, {
                                    duration: delayBeforeSuspense,
                                    onComplete: () => {
                                        if (!suspenseReelIndex) {
                                            GameSignals.symbolsDrop.emit()
                                        }
                                        suspenseReelIndex++
                                        reel.suspenseSpinStage2(suspenseReelIndex === cascadeReelsCount)
                                    },
                                })
                            })
                        }
                    },
                })
                return
            }

            gsap.to(reel, {
                duration: delay,
                onComplete: () => {
                    if (!cascadeSpinIndex) {
                        GameSignals.symbolsDrop.emit()
                    }
                    cascadeSpinIndex++
                    reel.cascadeSpin(cascadeSpinIndex === cascadeReelsCount)
                },
            })
        })
    }

    protected onShowRegularWin = (data: ShowRegularWinningsData) => {
        this.symbolsShadowIn([SpecialSymbolType.MULTIPLIER])
        this._pendingRegularOutAnimations = 0

        data.winPositions.forEach((position: number[]) => {
            const symbol: SymbolContainer = this._reels[position[0]].symbols[position[1]]
            symbol.shadowOut(false)

            if (symbol.index !== SpecialSymbolType.SCATTER && symbol.index !== SpecialSymbolType.MULTIPLIER) {
                if (this.isHighPaySymbol(symbol.index) || this.isLowPaySymbol(symbol.index)) {
                    this._pendingRegularOutAnimations++
                }

                if (symbol.index === SpecialSymbolType.MULTIPLIER && this._isFirstWildAnimation) {
                    this._isFirstWildAnimation = false
                    gsap.to(symbol, {
                        duration: 0.1,
                        onComplete: () => {
                            this.playWinningSymbolAnimation(symbol)
                        },
                    })
                    return
                }

                this.playWinningSymbolAnimation(symbol)
            }
        })

        if (this._pendingRegularOutAnimations === 0) {
            GameSignals.regularWinOutAnimationsComplete.emit()
        }
    }

    private playWinningSymbolAnimation(symbol: SymbolContainer): void {
        if (this.isHighPaySymbol(symbol.index) || this.isLowPaySymbol(symbol.index)) {
            const type = this.isHighPaySymbol(symbol.index) ? 'HP' : 'LP'
            let completed = false
            const onOutComplete = () => {
                if (completed) {
                    return
                }

                completed = true
                symbol.stopHpOutOverlayAnimation()
                console.debug(`[WIN_OUT_DEBUG][${type}] out complete`, { index: symbol.index })
                symbol.visible = false
                this.onRegularOutAnimationComplete()
            }

            symbol.playAnimation(['win', 'out'], [false, false], onOutComplete)

            const winDuration = symbol.getCurrentTrackDuration()
            const overlayDelay = winDuration > 0 ? winDuration : 0.1
            gsap.delayedCall(overlayDelay, () => {
                if (!completed && symbol.visible) {
                    console.debug(`[WIN_OUT_DEBUG][${type}] start overlay at out start`, {
                        index: symbol.index,
                        overlayDelay,
                    })
                    symbol.playHpOutOverlayAnimation()
                }
            })

            return
        }

        symbol.playAnimation('win', false, () => {
            symbol.visible = false
        })
    }

    private isHighPaySymbol(symbolIndex: number): boolean {
        return (
            symbolIndex === SymbolType.HP_1 ||
            symbolIndex === SymbolType.HP_2 ||
            symbolIndex === SymbolType.HP_3 ||
            symbolIndex === SymbolType.HP_4
        )
    }

    private isLowPaySymbol(symbolIndex: number): boolean {
        return (
            symbolIndex === SymbolType.LP_1 ||
            symbolIndex === SymbolType.LP_2 ||
            symbolIndex === SymbolType.LP_3 ||
            symbolIndex === SymbolType.LP_4 ||
            symbolIndex === SymbolType.LP_5
        )
    }

    private onRegularOutAnimationComplete(): void {
        if (this._pendingRegularOutAnimations <= 0) {
            return
        }

        this._pendingRegularOutAnimations--

        if (this._pendingRegularOutAnimations === 0) {
            GameSignals.regularWinOutAnimationsComplete.emit()
        }
    }

    protected changeSpinSettings(isQuickSpin: boolean) {
        this._speedSettings = isQuickSpin ? Facade.reelsConfig.QUICK : Facade.reelsConfig.NORMAL
    }

    protected onCreateReels = (data: IInitReelsData) => {
        Facade.reelsStates = []
        this._reels = []

        this._reelsContainerBotUnmasked = new PIXI.Container()
        this._reelsContainerBotUnmasked.name = ContainersNames.BOTTOM_UNMASKED_CONTAINER

        this._reelsContainer = new PIXI.Container()
        this._reelsContainer.name = ContainersNames.REELS_CONTAINER

        this._reelsContainerTopUnmasked = new PIXI.Container()
        this._reelsContainerTopUnmasked.name = ContainersNames.TOP_UNMASKED_CONTAINER

        this.addChild(this._reelsContainerBotUnmasked, this._reelsContainer, this._reelsContainerTopUnmasked)

        this.selectReelsStrip(ReelsStripConfig.NORMAL_GAME_SYMBOLS_STRIP)
        this.createSymbolsLayers()

        this._reels = this.createReels(data)

        this.createReelsMask()
        this.setupMobileSwipeSpin()
    }

    // private onCompleteSplashScreenState = () => {
    //     const minVisibleY = 0
    //     const maxVisibleY = ReelsContainerHelper.calculateReelHeight() * (Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL - 1)
    //
    //     this._reels.forEach((reel: ReelContainer) => {
    //         reel.symbols.forEach((symbol: SymbolContainer) => {
    //             const isVisible = symbol.y >= minVisibleY && symbol.y <= maxVisibleY
    //
    //             if (isVisible && this.isHighPaySymbol(symbol.index)) {
    //                 symbol.playLandingLoopOverlayOnStatic()
    //             }
    //         })
    //     })
    // }

    protected createSymbolsLayers() {
        const layersCount = SymbolLayerType.COUNT
        this._symbolLayers = []

        for (let index = 0; index < layersCount; index++) {
            const layer = new PIXI.Container()
            layer.name = `Layer #${SymbolLayerType[index]}`
            this._symbolLayers.push(layer)

            if (index === SymbolLayerType.UNMASKED_STANDARD_SYMBOLS) {
                this._reelsContainerBotUnmasked.addChild(layer)
            } else if (index > SymbolLayerType.UNMASKED_STANDARD_SYMBOLS) {
                this._reelsContainerTopUnmasked.addChild(layer)
            } else {
                this._reelsContainer.addChild(layer)
            }
        }
    }

    protected createReels(data: IInitReelsData): ReelContainer[] {
        const layersCount = SymbolLayerType.COUNT
        const reels: ReelContainer[] = []

        data.reels.forEach((reel: number[], index: number) => {
            const _reel: ReelContainer = new ReelContainer({
                reelIndex: index,
                reelGrid: data.reels[index],
                originalGrid: data.originalGrid[index],
                activeReelStrip: this._activeReelsStrip[index],
            })

            reels.push(_reel)

            const offset = ReelsContainerHelper.calculateReelWidth() * index

            for (let index = 0; index < layersCount; index++) {
                const layer = _reel.symbolLayers[index]
                layer.x += offset
                this._symbolLayers[index].addChild(layer)
            }
        })

        return reels
    }

    protected createReelsMask() {
        this._cropMask = new PIXI.Graphics()
        this._cropMask.name = ContainersNames.CROP_MASK
        this.redrawCropMask(ReelsContainer.REELS_MASK_DRAW_Y, ReelsContainer.REELS_MASK_HEIGHT)
        this._reelsContainer.mask = this._cropMask
        this._cropMask.position.set(ReelsContainer.REELS_MASK_X, ReelsContainer.BASE_GAME_MASK_Y)
        this.addChild(this._cropMask)
    }

    protected setCropMaskY(y: number) {
        if (!this._cropMask) {
            return
        }

        this._cropMask.y = y
    }

    protected redrawCropMask(y: number, height: number) {
        this._cropMask.clear()
        this._cropMask.beginFill(0x542dd, 0.5)
        this._cropMask.drawRect(0, y, ReelsContainer.REELS_MASK_WIDTH, height)
        this._cropMask.endFill()
    }

    private getMobileSwipeSpinConfig() {
        const config = Facade.defaultConfig?.mobileSwipeSpin

        if (typeof config === 'boolean') {
            return {
                enabled: config,
                requireConfirmation: false,
            }
        }

        if (!config || typeof config !== 'object') {
            return {
                enabled: false,
                requireConfirmation: false,
            }
        }

        return {
            enabled: config.enabled !== false,
            minDistance: config.minDistance,
            maxHorizontalDrift: config.maxHorizontalDrift,
            preventDefault: config.preventDefault,
            requireConfirmation: config.requireConfirmation === true,
        }
    }

    private setupMobileSwipeSpin() {
        const config = this.getMobileSwipeSpinConfig()

        this._mobileSwipeSpinController?.destroy()
        this._mobileSwipeSpinController = null

        if (!config.enabled) {
            this._isMobileSwipeSpinFeatureEnabled = false
            this._hasShownMobileSwipeSpinPopup = false
            this.hitArea = null
            return
        }

        this._isMobileSwipeSpinFeatureEnabled = !config.requireConfirmation
        this._hasShownMobileSwipeSpinPopup = false

        this.eventMode = 'static'
        this.hitArea = new PIXI.Rectangle(
            ReelsContainer.REELS_MASK_X,
            ReelsContainer.BASE_GAME_MASK_Y + ReelsContainer.REELS_MASK_DRAW_Y,
            ReelsContainer.REELS_MASK_WIDTH,
            ReelsContainer.REELS_MASK_HEIGHT
        )

        this._mobileSwipeSpinController = new MobileSwipeSpinController(this, () => {
            if (!this._isMobileSwipeSpinFeatureEnabled) {
                if (!this._hasShownMobileSwipeSpinPopup) {
                    GameSignals.requestSwipeFeaturePopup.emit()
                }

                return
            }

            UIAPI.emit(UIEvent.clicked_play)
        }, config)
    }

    private onEnableSwipeFeature = (enabled: boolean = false) => {
        this._isMobileSwipeSpinFeatureEnabled = enabled === true
    }

    private onSwipeFeaturePopupShown = () => {
        this._hasShownMobileSwipeSpinPopup = true
    }

    protected selectReelsStrip(reelsStrip: number[][]) {
        this._activeReelsStrip = reelsStrip

        for (let index = 0; index < this._reels.length; index++) {
            this._reels[index].selectReelsStrip(reelsStrip[index])
        }
    }

    protected changeMaskParams = (state: string) => {
        switch (state) {
            case 'start':
                this.redrawCropMask(-26, 777.5)
                break
            case 'stop':
                this.redrawCropMask(-26, 777.5)
                break
        }
    }

    protected onStartSpin = () => {
        if (this._isSpinning) {
            return
        }

        this._isQuickStop = Facade.isTurboSpin
        this._isSpinning = true
        this._scattersCount = 0
        this._immediatelyStopped = false
        // this._isAnticipationVisible = false
        this._cascadeIndex = 0

        this.changeSpinSettings(this._isQuickStop)
        this.changeMaskParams('start')

        this._reels.forEach((reel: ReelContainer, index: number) => {
            reel.reelGrid = null
            reel.originalReelGrid = null

            gsap.to(reel, {
                duration: this._speedSettings.DELAY_BETWEEN_REELS_ON_START * index,
                onComplete: () => {
                    reel.start()
                },
            })
        })
    }

    protected onReelJustTouched = (index: number, winningsSymbolsIndexes: number[]) => {
        const reel: ReelContainer = this._reels[index]
        let symbols: SymbolContainer[]

        if (winningsSymbolsIndexes) {
            symbols = []
            winningsSymbolsIndexes.forEach((symbolIndex: number) => {
                symbols.push(reel.symbols[symbolIndex])
            })
        } else {
            symbols = reel.symbols
        }

        for (const symbol of symbols) {
            switch (symbol.index) {
                case SpecialSymbolType.SCATTER:
                    this._scattersCount++
                    GameSignals.scatterLanded.emit(this._scattersCount, index)

                    reel.changeSymbolLayer(symbol, true)

                    const neededScattersCount: number = Facade.model.roundState.isFreeSpins ? 1 : 2

                    if (this._scattersCount > neededScattersCount) {
                        for (const reel of this._reels) {
                            gsap.to(reel, {
                                duration: 0.7,
                                onComplete: () => {
                                    if (
                                        reel.reelState === reel.reelStates.STOPPED &&
                                        this._cascadeIndex !== this._cascadeSpinsCount
                                    ) {
                                        reel.playScatterWaiting(reel.index)
                                    }
                                },
                            })
                        }
                    }
                    break
                case SpecialSymbolType.MULTIPLIER:
                    GameSignals.wildLanded.emit()
                    GameSignals.playMultiplierLandSound.emit(symbol.data.multiplier)
                    reel.changeSymbolLayer(symbol, true)
                    symbol.playAnimation('land')
                    break
            }
        }
    }

    protected onSpinStoping = (data: IStoppingReelsData) => {
        this._anticipationIndices = data.anticipationIndices

        this._reels.forEach((reel: ReelContainer, index: number) => {
            reel.setData(data.reels[index], data.originalGrid[index], this._isQuickStop)
        })

        gsap.to(this, {
            duration: this._speedSettings.DELAY_BEFORE_STOPPING,
            onComplete: () => {
                this.changeMaskParams('stop')
            },
        })

        gsap.to(this._reels, {
            duration: this._speedSettings.DELAY_BEFORE_STOPPING,
            onComplete: () => {
                let delay: number = 0
                this._reels.forEach((reel: ReelContainer, index: number) => {
                    let isSuspenseReel: boolean = false

                    if (!this._isQuickStop && this._anticipationIndices.includes(index) && !this._immediatelyStopped) {
                        isSuspenseReel = true
                    }

                    delay +=
                        this._speedSettings.DELAY_BETWEEN_REELS_ON_STOPPING +
                        (isSuspenseReel ? Facade.anticipationConfig.ANTICIPATION_REEL_TIME : 0)

                    gsap.to(reel, {
                        duration: delay,
                        onComplete: () => {
                            reel.stoppingReel(isSuspenseReel, false)
                            // if (this._anticipationIndices.length && this._anticipationIndices.includes(reel.index)) { // removing shadowIn in suspense reel
                            //     this.symbolsShadowIn([SpecialSymbolType.SCATTER], false)
                            // }
                            if (reel.index === Facade.reelsConfig.TOTAL_REELS - 1) {
                                this._isSpinning = false
                            }
                        },
                    })
                })
            },
        })
    }

    protected onImmediateStop = () => {
        if (this._immediatelyStopped) {
            return
        }

        this._immediatelyStopped = true
        GameSignals.reelsImmediatelyStopped.emit()

        gsap.killTweensOf(this._reels)
        this.changeMaskParams('stop')

        this._reels.forEach((reel: ReelContainer) => {
            if (reel.reelState !== reel.reelStates.STOPPING && reel.reelState !== reel.reelStates.STOPPED) {
                reel.stoppingReel(false, true)
                // if (this._anticipationIndices.length && this._anticipationIndices.includes(reel.index) && this._isAnticipationVisible) {
                //     this.symbolsShadowIn([SpecialSymbolType.SCATTER], false)
                // }
            }
        })

        this._isSpinning = false
    }

    protected onErrorStop = (data: IStoppingReelsData) => {
        this._isQuickStop = true
        if (this._isSpinning) {
            this.onSpinStoping(data)
        }
    }

    protected symbolsShadowIn = (exceptions: number[], animate: boolean = true) => {
        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                if (exceptions.length && exceptions.includes(symbol.index)) {
                    return
                }

                symbol.shadowIn(animate)
            })
        })
    }

    protected symbolsShadowOut = () => {
        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                symbol.shadowOut(true)
            })
        })
    }

    protected onActivateScatters = (data: FreeSpinsActivateData) => {
        this.symbolsShadowIn([SpecialSymbolType.SCATTER])

        for (const reel of this._reels) {
            reel.playScatterTriggerAnimation()
        }

        gsap.to(this, {
            duration: 2.5,
            onComplete: () => {
                this.symbolsShadowOut()
            },
        })
    }

    protected onFreeSpinsIntro = () => {
        this.setCropMaskY(ReelsContainer.FREE_SPINS_MASK_Y)
        this.selectReelsStrip(ReelsStripConfig.FREE_GAME_SYMBOLS_STRIP)
    }

    protected onFreeSpinsComplete = () => {
        this.setCropMaskY(ReelsContainer.BASE_GAME_MASK_Y)
        this.selectReelsStrip(ReelsStripConfig.NORMAL_GAME_SYMBOLS_STRIP)
    }

    protected onMultiplierState = (lastGridData: SymbolData[][]) => {
        const multipliersPositions: number[][] = []

        lastGridData.forEach((reelData: SymbolData[], reelIndex: number) => {
            reelData.forEach((symbolData: SymbolData, symbolIndex: number) => {
                if (symbolData?.id === SpecialSymbolType.MULTIPLIER) {
                    multipliersPositions.push([reelIndex, symbolIndex])
                }
            })
        })

        let delay: number = 0

        multipliersPositions.forEach((position: number[], index: number, array: number[][]) => {
            const symbol: SymbolContainer = this._reels[position[0]].symbols[position[1]]
            const lastPosition: boolean = index === array.length - 1

            gsap.to(symbol, {
                duration: delay,
                onComplete: () => {
                    symbol.showMultiplierLandAnimation(() => {
                        if (Facade.model?.roundState?.isFreeSpins) {
                            const p = symbol.getGlobalPosition(new PIXI.Point())
                            GameSignals.fsTopMultiplierCollectFly.emit({
                                fromX: p.x,
                                fromY: p.y,
                                multiplier: Number(symbol.data?.multiplier) || 0,
                            })
                        }

                        lastPosition
                            ? GameSignals.multiplierLanded.emit(symbol.data.multiplier)
                            : GameSignals.updateJarValue.emit(symbol.data.multiplier, true)
                    })
                },
            })
            delay += Facade.reelsConfig.DELAY_PER_MULTIPLIER_SYMBOLS
        })
    }
}
